import React, { useEffect, useMemo, useRef, useState } from 'react';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import ProfileCompletionFlow from '../components/dashboard/ProfileCompletionFlow';
import { useUsuario } from '../context/UsuarioContext';
import useCiudadesCatalog from '../hooks/useCiudadesCatalog';
import useProfesionesCatalog from '../hooks/useProfesionesCatalog';
import useOccupationFieldsCatalog from '../hooks/useOccupationFieldsCatalog';
import {
  SECTION_CONFIG,
  FIELD_CONFIG,
  createEmptyProfileForm,
  mapUsuarioToFormValues,
  buildUsuarioPayload,
  buildSectionPayload,
  FLOW_TOTAL_STEPS,
  ADDRESS_FIELD_NAMES,
  OPTIONAL_ADDRESS_FIELDS,
  formatAddressPreview,
} from '../constants/profileForm';

const ALLOWED_NUMBER_KEYS = new Set([
  'Backspace',
  'Tab',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Delete',
  'Home',
  'End',
  'Enter',
  'Escape',
]);

const sanitizeDigits = (value) => value.replace(/\D/g, '');
const sanitizePhoneValue = (value) => {
  if (!value) {
    return '';
  }
  const trimmed = value.replace(/\s+/g, '');
  if (trimmed.startsWith('+')) {
    const digits = sanitizeDigits(trimmed.slice(1));
    return digits.length > 0 ? `+${digits}` : '+';
  }
  return sanitizeDigits(trimmed);
};

const INTEGER_ONLY_MESSAGE = 'Solo se permiten números enteros.';
const REQUIRED_FIELD_MESSAGE = 'Completa este campo antes de continuar.';
const PHONE_FIELD_MESSAGE = 'Ingresa un número telefónico válido (ej. +573001112233).';
const EMAIL_FIELD_MESSAGE = 'Ingresa un correo electrónico válido.';
const LETTER_FIELD_MESSAGE = 'Ingresa solo letras sin números ni símbolos.';

const PHONE_REGEX = /^\+?\d{7,15}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const DIGIT_ONLY_FIELDS = new Set([
  'numeroDocumento',
  'direccionViaNumero',
  'direccionNumeroSecundario',
  'direccionNumeroPlaca',
]);
const PHONE_FIELDS = new Set(['celular', 'telefonoFijo']);
const EMAIL_FIELDS = new Set(['correoElectronico']);
const CITY_FIELDS = new Set(['ciudadExpedicionId', 'ciudadNacimientoId', 'ciudadResidenciaId']);
const OPTIONAL_FIELDS = new Set([
  'ciudadResidenciaId',
  'profesionDetalle',
  'direccionResidencia',
  ...OPTIONAL_ADDRESS_FIELDS,
]);
const ACCOUNT_FIELD_MAP = FIELD_CONFIG.reduce((acc, field) => {
  acc[field.name] = field;
  return acc;
}, {});
const LETTER_ONLY_FIELDS = new Set(['direccionViaLetra', 'direccionLetraSecundaria']);
const PROFESSION_REQUIRED_LEVELS = new Set([
  'TECNOLOGO',
  'PREGRADO',
  'ESPECIALIZACION',
  'MAESTRIA',
  'DOCTORADO',
  'POSTDOCTORADO',
]);
const OCCUPATION_REQUIRES_FIELD = new Set([
  'EMPLEADO',
  'INDEPENDIENTE',
  'EMPRESARIO',
  'OTRO',
]);

const isTruthy = (value) => value === true || value === 'true';

const isFalse = (value) => value === false || value === 'false';

const shouldDisplayField = (fieldName, values) => {
  const paisExpedicion = values?.paisExpedicion ?? '';
  const nacidoEnColombia = isTruthy(values?.nacidoEnColombia);
  const tieneOtraNacionalidad = isTruthy(values?.otraNacionalidad);
  const viveEnColombiaValor = values?.viveEnColombia ?? '';
  const viveEnColombia = isTruthy(viveEnColombiaValor);
  const noViveEnColombia = isFalse(viveEnColombiaValor);
  const mostrarDireccion = viveEnColombia || values?.paisResidencia === 'CO';
  const nivelEstudios = values?.nivelEstudios ?? '';
  const mostrarProfesion = PROFESSION_REQUIRED_LEVELS.has(nivelEstudios);
  const ocupacion = values?.ocupacion ?? '';
  const mostrarCampoOcupacion = OCCUPATION_REQUIRES_FIELD.has(ocupacion);

  switch (fieldName) {
    case 'departamentoExpedicion':
    case 'ciudadExpedicionId':
      return paisExpedicion === 'CO';
    case 'ciudadExpedicionOtra':
      return Boolean(paisExpedicion) && paisExpedicion !== 'CO';
    case 'paisNacimiento':
      return !nacidoEnColombia;
    case 'departamentoNacimiento':
    case 'ciudadNacimientoId':
      return nacidoEnColombia;
    case 'paisOtraNacionalidad':
      return tieneOtraNacionalidad;
    case 'paisResidencia':
      return noViveEnColombia;
    case 'ciudadResidenciaId':
      return mostrarDireccion;
    case 'profesionId':
    case 'profesionDetalle':
      return mostrarProfesion;
    case 'campoOcupacionId':
      return mostrarCampoOcupacion;
    case 'direccionResidencia':
    case 'direccionViaTipo':
    case 'direccionViaNumero':
    case 'direccionViaLetra':
    case 'direccionBis':
    case 'direccionCuadrante':
    case 'direccionNumeroSecundario':
    case 'direccionLetraSecundaria':
    case 'direccionNumeroPlaca':
    case 'direccionComplemento':
      return mostrarDireccion;
    default:
      return true;
  }
};

const applyDependentDefaults = (values) => {
  const next = { ...values };
  const paisExpedicion = next.paisExpedicion ?? '';
  if (paisExpedicion === 'CO') {
    next.ciudadExpedicionOtra = '';
  } else if (paisExpedicion) {
    next.departamentoExpedicion = '';
    next.ciudadExpedicionId = '';
  } else {
    next.departamentoExpedicion = '';
    next.ciudadExpedicionId = '';
    next.ciudadExpedicionOtra = '';
  }

  if (isTruthy(next.nacidoEnColombia)) {
    next.paisNacimiento = 'CO';
  } else if (next.nacidoEnColombia === 'false') {
    if (next.paisNacimiento === 'CO') {
      next.paisNacimiento = '';
    }
    next.departamentoNacimiento = '';
    next.ciudadNacimientoId = '';
  }

  if (next.paisResidencia && next.paisResidencia !== 'CO') {
    next.ciudadResidenciaId = '';
  }

  if (isFalse(next.otraNacionalidad)) {
    next.paisOtraNacionalidad = '';
  }

  const nivelEstudios = next.nivelEstudios ?? '';
  if (!PROFESSION_REQUIRED_LEVELS.has(nivelEstudios)) {
    next.profesionId = '';
    next.profesionDetalle = '';
  }

  const ocupacion = next.ocupacion ?? '';
  if (!OCCUPATION_REQUIRES_FIELD.has(ocupacion)) {
    next.campoOcupacionId = '';
  }

  if (isTruthy(next.viveEnColombia)) {
    next.paisResidencia = 'CO';
  } else if (isFalse(next.viveEnColombia)) {
    if (next.paisResidencia === 'CO') {
      next.paisResidencia = '';
    }
    ADDRESS_FIELD_NAMES.forEach((field) => {
      next[field] = '';
    });
    next.direccionResidencia = '';
  }

  next.direccionResidencia = formatAddressPreview(next);

  return next;
};

const getNormalizedValue = (field, value) => {
  if (value === undefined || value === null) {
    return '';
  }
  const stringValue = typeof value === 'string' ? value : `${value}`;

  if (PHONE_FIELDS.has(field.name)) {
    return sanitizePhoneValue(stringValue);
  }

  if (EMAIL_FIELDS.has(field.name)) {
    return stringValue.trim();
  }

  if (field.type === 'number') {
    const digitsOnly = sanitizeDigits(stringValue);
    if (digitsOnly.length === 0) {
      return '';
    }
    return `${Number.parseInt(digitsOnly, 10)}`;
  }

  if (DIGIT_ONLY_FIELDS.has(field.name)) {
    return sanitizeDigits(stringValue);
  }

  if (LETTER_ONLY_FIELDS.has(field.name)) {
    return stringValue.replace(/[^A-Za-z]/g, '').toUpperCase();
  }

  return stringValue;
};

const getValidationError = (field, value, values = {}) => {
  const normalizedRaw = value ?? '';
  const normalized =
    typeof normalizedRaw === 'string' ? normalizedRaw : `${normalizedRaw}`;

  if (field.name === 'profesionId') {
    const nivel = values?.nivelEstudios ?? '';
    if (PROFESSION_REQUIRED_LEVELS.has(nivel)) {
      return normalized ? null : REQUIRED_FIELD_MESSAGE;
    }
    return null;
  }

  if (field.name === 'profesionDetalle') {
    if (!normalized) {
      return null;
    }
    if (/[<>]/.test(normalized)) {
      return 'No uses los caracteres "<" o ">".';
    }
    return null;
  }

  if (field.name === 'campoOcupacionId') {
    const ocupacionActual = values?.ocupacion ?? '';
    if (!ocupacionActual || !OCCUPATION_REQUIRES_FIELD.has(ocupacionActual)) {
      return null;
    }
    return normalized ? null : REQUIRED_FIELD_MESSAGE;
  }

  if (PHONE_FIELDS.has(field.name)) {
    if (!normalized) {
      return REQUIRED_FIELD_MESSAGE;
    }
    if (!PHONE_REGEX.test(normalized)) {
      return PHONE_FIELD_MESSAGE;
    }
    return null;
  }

  if (EMAIL_FIELDS.has(field.name)) {
    if (!normalized) {
      return OPTIONAL_FIELDS.has(field.name) ? null : REQUIRED_FIELD_MESSAGE;
    }
    if (!EMAIL_REGEX.test(normalized)) {
      return EMAIL_FIELD_MESSAGE;
    }
    return null;
  }

  const isOptionalField = OPTIONAL_FIELDS.has(field.name);

  if (field.type === 'number' || DIGIT_ONLY_FIELDS.has(field.name)) {
    if (!normalized) {
      return isOptionalField ? null : REQUIRED_FIELD_MESSAGE;
    }
    if (!/^\d+$/.test(normalized)) {
      return INTEGER_ONLY_MESSAGE;
    }
    return null;
  }

  if (LETTER_ONLY_FIELDS.has(field.name)) {
    if (!normalized) {
      return isOptionalField ? null : REQUIRED_FIELD_MESSAGE;
    }
    if (!/^[A-Za-z]+$/.test(normalized)) {
      return LETTER_FIELD_MESSAGE;
    }
    return null;
  }

  if (field.type === 'boolean') {
    if (normalized === 'true' || normalized === 'false') {
      return null;
    }
    return isOptionalField ? null : REQUIRED_FIELD_MESSAGE;
  }

  if (field.type === 'date') {
    return normalized ? null : REQUIRED_FIELD_MESSAGE;
  }

  if (typeof normalized === 'string') {
    if (normalized.trim().length === 0) {
      return OPTIONAL_FIELDS.has(field.name) ? null : REQUIRED_FIELD_MESSAGE;
    }
    return null;
  }

  if (normalized) {
    return null;
  }
  return OPTIONAL_FIELDS.has(field.name) ? null : REQUIRED_FIELD_MESSAGE;
};

const normalizeFormValues = (values) => {
  const normalized = { ...values };
  Object.keys(normalized).forEach((key) => {
    const field = ACCOUNT_FIELD_MAP[key];
    if (!field) {
      return;
    }
    normalized[key] = getNormalizedValue(field, normalized[key]);
  });
  return normalized;
};

const PersonasAccount = () => {
  const { usuario, saveUsuario, hasLoaded, isFetching, error: usuarioError } = useUsuario();
  const [formValues, setFormValues] = useState(() => createEmptyProfileForm());
  const [originalValues, setOriginalValues] = useState(() => createEmptyProfileForm());
  const [editingFields, setEditingFields] = useState({});
  const [activeSection, setActiveSection] = useState(SECTION_CONFIG[0].id);
  const [pendingSection, setPendingSection] = useState(null);
  const [showUnsavedPrompt, setShowUnsavedPrompt] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [isFlowVisible, setIsFlowVisible] = useState(false);
  const isLoading = !hasLoaded || isFetching;
  const completedSections = usuario?.seccionesPerfilCompletadas ?? [];
  const totalSections = FLOW_TOTAL_STEPS;
  const completedCount = completedSections.length;
  const hasFullCompletion =
    usuario?.formularioPerfilCompleto === true || completedCount >= totalSections;
  const needsProfileCompletion = hasLoaded && (!usuario || !hasFullCompletion);
  const remainingSections = Math.max(totalSections - completedCount, 0);
  const {
    catalog: ciudadCatalog,
    getCitiesForDepartment,
    cityMap: ciudadOptionMap,
    loading: ciudadOptionsLoading,
    error: ciudadOptionsError,
  } = useCiudadesCatalog();
  const {
    options: professionOptions,
    loading: professionOptionsLoading,
    error: professionOptionsError,
    refresh: refreshProfessionCatalog,
  } = useProfesionesCatalog();
  const {
    options: occupationFieldOptions,
    loading: occupationFieldLoading,
    error: occupationFieldError,
    refresh: refreshOccupationCatalog,
  } = useOccupationFieldsCatalog();
  const flowInitialValues = useMemo(() => {
    const base = usuario ? mapUsuarioToFormValues(usuario) : createEmptyProfileForm();
    const normalized = normalizeFormValues(base);
    return applyDependentDefaults(normalized);
  }, [usuario]);
  const getCityOptionsForField = (fieldName) => {
    switch (fieldName) {
      case 'ciudadExpedicionId':
        return getCitiesForDepartment(formValues.departamentoExpedicion) ?? [];
      case 'ciudadNacimientoId':
        return getCitiesForDepartment(formValues.departamentoNacimiento) ?? [];
      case 'ciudadResidenciaId':
        return ciudadCatalog.allCities ?? [];
      default:
        return ciudadCatalog.allCities ?? [];
    }
  };
  const prevNeedsRef = useRef(false);
  const professionCatalogRequestedRef = useRef(false);
  const occupationCatalogRequestedRef = useRef(false);

  useEffect(() => {
    if (needsProfileCompletion) {
      if (!prevNeedsRef.current) {
        setIsFlowVisible(true);
      }
      prevNeedsRef.current = true;
      return;
    }
    prevNeedsRef.current = false;
    setIsFlowVisible(false);
  }, [needsProfileCompletion]);

  useEffect(() => {
    if (!hasLoaded) {
      return;
    }

    if (!usuario) {
      const empty = createEmptyProfileForm();
      setFormValues({ ...empty });
      setOriginalValues({ ...empty });
      setEditingFields({});
      setSuccessMessage('');
      setFieldErrors({});
      setErrorMessage('');
      return;
    }

    const mapped = mapUsuarioToFormValues(usuario);
    const normalized = normalizeFormValues(mapped);
    const adjusted = applyDependentDefaults(normalized);
    setFormValues({ ...adjusted });
    setOriginalValues({ ...adjusted });
    setEditingFields({});
    setSuccessMessage('');
    setErrorMessage('');
    setFieldErrors({});
  }, [hasLoaded, usuario]);

  useEffect(() => {
    const nivel = formValues?.nivelEstudios ?? '';
    const requiereProfesion = PROFESSION_REQUIRED_LEVELS.has(nivel);
    if (!requiereProfesion) {
      professionCatalogRequestedRef.current = false;
      return;
    }
    if (professionOptions.length > 0) {
      professionCatalogRequestedRef.current = true;
      return;
    }
    if (professionOptionsLoading || professionCatalogRequestedRef.current) {
      return;
    }
    (async () => {
      try {
        professionCatalogRequestedRef.current = true;
        await refreshProfessionCatalog();
      } catch {
        // if falla dejamos el flag en true para evitar loop automático
      }
    })();
  }, [
    formValues?.nivelEstudios,
    professionOptions.length,
    professionOptionsLoading,
    refreshProfessionCatalog,
  ]);

  useEffect(() => {
    const ocupacionActual = formValues?.ocupacion ?? '';
    const requiereCampo = OCCUPATION_REQUIRES_FIELD.has(ocupacionActual);
    if (!requiereCampo) {
      occupationCatalogRequestedRef.current = false;
      return;
    }
    if (occupationFieldOptions.length > 0) {
      occupationCatalogRequestedRef.current = true;
      return;
    }
    if (occupationFieldLoading || occupationCatalogRequestedRef.current) {
      return;
    }
    (async () => {
      try {
        occupationCatalogRequestedRef.current = true;
        await refreshOccupationCatalog();
      } catch {
        // evitamos loop automático
      }
    })();
  }, [
    formValues?.ocupacion,
    occupationFieldOptions.length,
    occupationFieldLoading,
    refreshOccupationCatalog,
  ]);

  useEffect(() => {
    if (!usuarioError) {
      return;
    }
    const message =
      usuarioError instanceof Error && usuarioError.message
        ? usuarioError.message
        : 'No fue posible cargar tu información en este momento.';
    setErrorMessage(message);
  }, [usuarioError]);

  const handleFlowSectionSave = async (sectionId, { payload, formValues: sectionValues }) => {
    try {
      const sectionPayload =
        payload && Object.keys(payload).length > 0
          ? payload
          : buildSectionPayload(sectionValues, sectionId, { skipEmpty: true });

      if (!sectionPayload || Object.keys(sectionPayload).length === 0) {
        throw new Error('Completa al menos un campo de esta etapa antes de continuar.');
      }

      const existingCompleted = new Set(completedSections);
      existingCompleted.add(sectionId);
      const updatedCompleted = Array.from(existingCompleted);

      const dataToSave = {
        ...sectionPayload,
        seccionesPerfilCompletadas: updatedCompleted,
      };

      if (updatedCompleted.length >= totalSections) {
        dataToSave.formularioPerfilCompleto = true;
      }

      const updated = await saveUsuario(dataToSave);
      return updated;
    } catch (error) {
      if (error instanceof Error && error.message) {
        throw error;
      }
      throw new Error('No fue posible guardar esta etapa. Intenta nuevamente.');
    }
  };

  const handleFlowComplete = async (latestUsuario) => {
    const snapshot = latestUsuario ?? usuario;
    const completed = new Set(snapshot?.seccionesPerfilCompletadas ?? []);
    if (completed.size < totalSections) {
      const remaining = totalSections - completed.size;
      throw new Error(
        `Aún faltan ${remaining} etapa${remaining === 1 ? '' : 's'} por guardar.`,
      );
    }

    await saveUsuario({
      formularioPerfilCompleto: true,
      flujoPerfilMostrado: true,
      seccionesPerfilCompletadas: Array.from(completed),
    });
    setIsFlowVisible(false);
  };

  const handleFlowClose = () => {
    setIsFlowVisible(false);
  };

  const handleFlowOpen = () => {
    setIsFlowVisible(true);
  };

  const fieldsForActiveSection = useMemo(
    () => FIELD_CONFIG.filter((field) => field.section === activeSection),
    [activeSection],
  );

  const visibleFieldsForActiveSection = useMemo(
    () =>
      fieldsForActiveSection
        .filter((field) => !field.meta?.hideInForm)
        .filter((field) => shouldDisplayField(field.name, formValues)),
    [fieldsForActiveSection, formValues],
  );

  const isDirty = useMemo(
    () =>
      Object.keys(formValues).some(
        (key) => (formValues[key] ?? '') !== (originalValues[key] ?? ''),
      ),
    [formValues, originalValues],
  );

  const updateFieldError = (name, message) => {
    setFieldErrors((prev) => {
      if (message) {
        if (prev[name] === message) {
          return prev;
        }
        return { ...prev, [name]: message };
      }
      if (!prev[name]) {
        return prev;
      }
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleNumberKeyDown = () => (event) => {
    if (event.ctrlKey || event.metaKey) {
      const lower = event.key.toLowerCase();
      if (lower === 'a' || lower === 'c' || lower === 'v' || lower === 'x') {
        return;
      }
    }
    if (ALLOWED_NUMBER_KEYS.has(event.key) || /^\d$/.test(event.key)) {
      return;
    }
    event.preventDefault();
  };

  const handleNumberPaste = () => (event) => {
    const text = event.clipboardData?.getData('text') ?? '';
    if (/^\d*$/.test(text)) {
      return;
    }
    event.preventDefault();
  };

  const handleChange = (field) => (event) => {
    const { name } = field;
    const rawValue = event.target.value;
    setSuccessMessage('');
    setErrorMessage('');

    const normalized = getNormalizedValue(field, rawValue);
    let nextValues = null;
    setFormValues((prev) => {
      const next = { ...prev, [name]: normalized };

      if (name === 'nacidoEnColombia') {
        if (normalized === 'true') {
          next.paisNacimiento = 'CO';
        } else if (normalized === 'false') {
          next.paisNacimiento =
            prev.paisNacimiento && prev.paisNacimiento !== 'CO'
              ? prev.paisNacimiento
              : '';
          next.departamentoNacimiento = '';
          next.ciudadNacimientoId = '';
        } else {
          next.paisNacimiento = '';
          next.departamentoNacimiento = '';
          next.ciudadNacimientoId = '';
        }
      }

      if (name === 'paisNacimiento') {
        if (normalized === 'CO') {
          next.nacidoEnColombia = 'true';
        } else if (normalized) {
          next.nacidoEnColombia = 'false';
        }
      }

      if (name === 'paisExpedicion') {
        if (normalized === 'CO') {
          next.ciudadExpedicionOtra = '';
        } else if (normalized) {
          next.departamentoExpedicion = '';
          next.ciudadExpedicionId = '';
        } else {
          next.departamentoExpedicion = '';
          next.ciudadExpedicionId = '';
          next.ciudadExpedicionOtra = '';
        }
      }

      if (name === 'paisResidencia') {
        if (normalized !== 'CO') {
          next.ciudadResidenciaId = '';
        }
        if (normalized === 'CO') {
          next.viveEnColombia = 'true';
        } else if (normalized) {
          next.viveEnColombia = 'false';
        }
      }

      if (name === 'departamentoExpedicion') {
        next.ciudadExpedicionId = '';
      }

      if (name === 'departamentoNacimiento') {
        next.ciudadNacimientoId = '';
      }

      if (name === 'otraNacionalidad') {
        if (normalized !== 'true') {
          next.paisOtraNacionalidad = '';
        }
      }

      if (name === 'nivelEstudios') {
        if (!PROFESSION_REQUIRED_LEVELS.has(normalized)) {
          next.profesionId = '';
          next.profesionDetalle = '';
        }
        if (
          PROFESSION_REQUIRED_LEVELS.has(normalized) &&
          !professionOptionsLoading &&
          professionOptions.length === 0 &&
          (!professionCatalogRequestedRef.current || professionOptionsError)
        ) {
          if (!professionCatalogRequestedRef.current) {
            professionCatalogRequestedRef.current = true;
          }
          refreshProfessionCatalog().catch(() => {});
        }
      }

      if (name === 'ocupacion') {
        if (!OCCUPATION_REQUIRES_FIELD.has(normalized)) {
          next.campoOcupacionId = '';
        }
        if (
          OCCUPATION_REQUIRES_FIELD.has(normalized) &&
          !occupationFieldLoading &&
          occupationFieldOptions.length === 0 &&
          (!occupationCatalogRequestedRef.current || occupationFieldError)
        ) {
          if (!occupationCatalogRequestedRef.current) {
            occupationCatalogRequestedRef.current = true;
          }
          refreshOccupationCatalog().catch(() => {});
        }
      }

      if (name === 'viveEnColombia') {
        if (normalized === 'true') {
          next.paisResidencia = 'CO';
        } else if (normalized === 'false') {
          if (prev.paisResidencia === 'CO') {
            next.paisResidencia = '';
          }
          ADDRESS_FIELD_NAMES.forEach((fieldName) => {
            next[fieldName] = '';
          });
          next.direccionResidencia = '';
        }
      }

      next.direccionResidencia = formatAddressPreview(next);
      nextValues = next;

      return next;
    });

    const evaluationValues = nextValues ?? formValues;
    const validationError = getValidationError(field, normalized, evaluationValues);
    updateFieldError(name, validationError);

    if (name === 'nacidoEnColombia') {
      updateFieldError('paisNacimiento', null);
      updateFieldError('departamentoNacimiento', null);
      updateFieldError('ciudadNacimientoId', null);
    }

    if (name === 'paisNacimiento') {
      updateFieldError('nacidoEnColombia', null);
      updateFieldError('departamentoNacimiento', null);
      updateFieldError('ciudadNacimientoId', null);
    }

    if (name === 'paisExpedicion') {
      updateFieldError('departamentoExpedicion', null);
      updateFieldError('ciudadExpedicionId', null);
      updateFieldError('ciudadExpedicionOtra', null);
    }

    if (name === 'departamentoExpedicion') {
      updateFieldError('ciudadExpedicionId', null);
    }

    if (name === 'departamentoNacimiento') {
      updateFieldError('ciudadNacimientoId', null);
    }

    if (name === 'otraNacionalidad') {
      updateFieldError('paisOtraNacionalidad', null);
    }

    if (name === 'nivelEstudios') {
      const profesionField = ACCOUNT_FIELD_MAP.profesionId;
      if (profesionField) {
        updateFieldError(
          'profesionId',
          getValidationError(
            profesionField,
            evaluationValues?.profesionId ?? '',
            evaluationValues,
          ),
        );
      }
      const profesionDetalleField = ACCOUNT_FIELD_MAP.profesionDetalle;
      if (profesionDetalleField) {
        updateFieldError(
          'profesionDetalle',
          getValidationError(
            profesionDetalleField,
            evaluationValues?.profesionDetalle ?? '',
            evaluationValues,
          ),
        );
      }
    }

    if (name === 'ocupacion') {
      const campoField = ACCOUNT_FIELD_MAP.campoOcupacionId;
      if (campoField) {
        updateFieldError(
          'campoOcupacionId',
          getValidationError(
            campoField,
            evaluationValues?.campoOcupacionId ?? '',
            evaluationValues,
          ),
        );
      }
    }

    if (name === 'viveEnColombia') {
      updateFieldError('paisResidencia', null);
      ADDRESS_FIELD_NAMES.forEach((fieldName) => {
        updateFieldError(fieldName, null);
      });
      updateFieldError('direccionResidencia', null);
    }

    if (name === 'paisResidencia') {
      updateFieldError('viveEnColombia', null);
    }
  };

  const toggleFieldEditing = (name) => {
    const fieldDefinition = FIELD_CONFIG.find((field) => field.name === name);
    if (
      fieldDefinition?.inputProps?.readOnly === true &&
      !fieldDefinition?.meta?.addressGroupRoot
    ) {
      return;
    }
    setEditingFields((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
    setSuccessMessage('');
    setErrorMessage('');
  };

  const renderAddressControl = (fieldName) => {
    const definition = ACCOUNT_FIELD_MAP[fieldName];
    if (!definition) {
      return null;
    }
    const value = formValues[fieldName] ?? '';
    const hasError = Boolean(fieldErrors[fieldName]);
    const commonProps = {
      id: fieldName,
      name: fieldName,
      value,
      onChange: handleChange(definition),
      className: hasError
        ? 'w-full rounded-lg border border-red-500 bg-[#ffffff] px-3 py-2 text-sm text-[#002E6D] focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-200'
        : 'w-full rounded-lg border border-blue-300 bg-[#ffffff] px-3 py-2 text-sm text-[#002E6D] focus:border-[#0054c9] focus:outline-none focus:ring-2 focus:ring-[#80aefc]',
      'aria-invalid': hasError || undefined,
    };

    const renderError = () =>
      hasError ? (
        <p className="mt-1 text-xs font-semibold text-red-600">
          {fieldErrors[fieldName]}
        </p>
      ) : null;

    const label = (
      <label htmlFor={fieldName} className="text-xs font-semibold text-[#002E6D]">
        {definition.label}
      </label>
    );

    if (definition.options && Array.isArray(definition.options)) {
      return (
        <div className="flex flex-col gap-1">
          {label}
          <select {...commonProps}>
            <option value="">Selecciona una opción</option>
            {definition.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {renderError()}
        </div>
      );
    }

    if (definition.type === 'boolean') {
      return (
        <div className="flex flex-col gap-1">
          {label}
          <select {...commonProps}>
            <option value="">Selecciona una opción</option>
            <option value="true">Sí</option>
            <option value="false">No</option>
          </select>
          {renderError()}
        </div>
      );
    }

    const inputProps = definition.inputProps ?? {};

    return (
      <div className="flex flex-col gap-1">
        {label}
        <input
          {...commonProps}
          {...inputProps}
          type={inputProps.type ?? 'text'}
        />
        {renderError()}
      </div>
    );
  };

  const renderAddressEditor = () => {
    const preview = formatAddressPreview(formValues);
    const rows = [
      ['direccionViaTipo', 'direccionViaNumero', 'direccionViaLetra', 'direccionBis', 'direccionCuadrante'],
      ['direccionNumeroSecundario', 'direccionLetraSecundaria', 'direccionNumeroPlaca', 'direccionComplemento'],
    ];

    return (
      <div className="flex flex-col gap-4">
        {rows.map((row, index) => (
          <div
            key={index}
            className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5"
          >
            {row.map((fieldName) => (
              <React.Fragment key={fieldName}>{renderAddressControl(fieldName)}</React.Fragment>
            ))}
          </div>
        ))}
        <div className="rounded-lg border border-blue-300 bg-[#caddff] px-3 py-2 text-sm text-[#002E6D]">
          {preview
            ? preview
            : 'Completa los campos de la dirección para generar el formato estándar.'}
        </div>
      </div>
    );
  };

  const renderInput = (field) => {
    if (field.meta?.addressGroupRoot) {
      return renderAddressEditor();
    }

    if (!shouldDisplayField(field.name, formValues)) {
      return null;
    }
    const { type: inputTypeOverride, ...restInputProps } = field.inputProps ?? {};
    const hasError = Boolean(fieldErrors[field.name]);
    const isCityField = CITY_FIELDS.has(field.name);
    let effectiveOptions = [];
    let selectLoading = false;
    let selectError = null;
    if (isCityField) {
      effectiveOptions = getCityOptionsForField(field.name);
      selectLoading = ciudadOptionsLoading;
      selectError = ciudadOptionsError;
    } else if (field.name === 'profesionId') {
      effectiveOptions = professionOptions;
      selectLoading = professionOptionsLoading;
      selectError = professionOptionsError;
    } else if (field.name === 'campoOcupacionId') {
      effectiveOptions = occupationFieldOptions;
      selectLoading = occupationFieldLoading;
      selectError = occupationFieldError;
    } else if (Array.isArray(field.options)) {
      effectiveOptions = field.options;
    }
    const canRetryProfession =
      field.name === 'profesionId' && selectError && !professionOptionsLoading;
    const canRetryOccupation =
      field.name === 'campoOcupacionId' && selectError && !occupationFieldLoading;
    const handleRetryClick = () => {
      if (canRetryProfession) {
        professionCatalogRequestedRef.current = true;
        refreshProfessionCatalog().catch(() => {});
      } else if (canRetryOccupation) {
        occupationCatalogRequestedRef.current = true;
        refreshOccupationCatalog().catch(() => {});
      }
    };
    const inputBaseClass =
      'w-full rounded-lg border border-blue-300 bg-[#caddff] px-3 py-2 text-sm text-[#002E6D] focus:border-[#0054c9] focus:outline-none focus:ring-2 focus:ring-[#80aefc]';
    const inputClassName = hasError
      ? `${inputBaseClass} border-red-500 focus:border-red-500 focus:ring-red-200`
      : inputBaseClass;
    const commonProps = {
      id: field.name,
      name: field.name,
      value: formValues[field.name] ?? '',
      onChange: handleChange(field),
      className: inputClassName,
      'aria-invalid': hasError || undefined,
    };
    const combinedProps = { ...commonProps, ...restInputProps };

    if (DIGIT_ONLY_FIELDS.has(field.name) && field.type !== 'number') {
      combinedProps.inputMode = combinedProps.inputMode ?? 'numeric';
      combinedProps.pattern = combinedProps.pattern ?? '\\d*';
    }

    if (PHONE_FIELDS.has(field.name)) {
      combinedProps.inputMode = combinedProps.inputMode ?? 'tel';
      combinedProps.pattern = combinedProps.pattern ?? '\\+?\\d*';
      combinedProps.placeholder =
        combinedProps.placeholder ?? 'Ej: +573001112233';
    }

    if (isCityField || field.meta?.catalog || (field.options && Array.isArray(field.options))) {
      const selectDisabled =
        Boolean(combinedProps.disabled) || selectLoading || isSaving;
      const selectProps = { ...combinedProps, disabled: selectDisabled };
      const placeholderLabel = selectLoading
        ? 'Cargando opciones...'
        : 'Selecciona una opción';
      return (
        <>
          <select {...selectProps}>
            <option value="">{placeholderLabel}</option>
            {effectiveOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {hasError ? (
            <p className="mt-1 text-xs font-semibold text-red-600">{fieldErrors[field.name]}</p>
          ) : selectError ? (
            <p className="mt-1 text-xs font-semibold text-red-600">
              {selectError.message ||
                'No fue posible cargar las opciones. Intenta nuevamente.'}
              {(canRetryProfession || canRetryOccupation) && (
                <button
                  type="button"
                  className="ml-2 underline"
                  onClick={handleRetryClick}
                >
                  Reintentar
                </button>
              )}
            </p>
          ) : null}
        </>
      );
    }

    if (field.type === 'boolean') {
      return (
        <>
          <select {...combinedProps}>
            <option value="">Selecciona una opción</option>
            <option value="true">Sí</option>
            <option value="false">No</option>
          </select>
          {hasError ? (
            <p className="mt-1 text-xs font-semibold text-red-600">{fieldErrors[field.name]}</p>
          ) : null}
        </>
      );
    }

    if (field.type === 'date') {
      return (
        <>
          <input
            {...combinedProps}
            type="date"
          />
          {hasError ? (
            <p className="mt-1 text-xs font-semibold text-red-600">{fieldErrors[field.name]}</p>
          ) : null}
        </>
      );
    }

    if (field.type === 'number') {
      const { inputMode, pattern, onKeyDown, onPaste, ...restProps } = combinedProps;
      const enforceKeyDown = handleNumberKeyDown();
      const enforcePaste = handleNumberPaste();
      return (
        <>
          <input
            {...restProps}
            type="number"
            inputMode={inputMode ?? 'numeric'}
            pattern={pattern ?? '[0-9]*'}
            onChange={handleChange(field)}
            onKeyDown={(event) => {
              enforceKeyDown(event);
              if (typeof onKeyDown === 'function') {
                onKeyDown(event);
              }
            }}
            onPaste={(event) => {
              enforcePaste(event);
              if (typeof onPaste === 'function') {
                onPaste(event);
              }
            }}
          />
          {hasError ? (
            <p className="mt-1 text-xs font-semibold text-red-600">{fieldErrors[field.name]}</p>
          ) : null}
        </>
      );
    }

    return (
      <>
        <input
          {...combinedProps}
          type={inputTypeOverride ?? 'text'}
        />
        {hasError ? (
          <p className="mt-1 text-xs font-semibold text-red-600">{fieldErrors[field.name]}</p>
        ) : null}
      </>
    );
  };

  const displayValue = (field) => {
    if (field.name === 'direccionResidencia') {
      const preview = formatAddressPreview(formValues);
      return preview ? preview : 'Sin información';
    }

    const value = formValues[field.name];
    if (!value) {
      return 'Sin información';
    }

    if (CITY_FIELDS.has(field.name)) {
      return ciudadOptionMap[value] ?? value;
    }

    if (field.type === 'boolean') {
      if (value === 'true') return 'Sí';
      if (value === 'false') return 'No';
      return 'Sin información';
    }

    if (field.options && Array.isArray(field.options)) {
      const match = field.options.find((option) => option.value === value);
      if (match) {
        return match.label;
      }
    }

    return value;
  };

  const saveChanges = async () => {
    const normalizedUpdates = {};
    let needsNormalization = false;
    const evaluationValues = { ...formValues };

    fieldsForActiveSection.forEach((field) => {
      const currentValue = evaluationValues[field.name];
      const normalized = getNormalizedValue(field, currentValue);
      if (normalized !== currentValue) {
        normalizedUpdates[field.name] = normalized;
        needsNormalization = true;
        evaluationValues[field.name] = normalized;
      }
    });

    const visibleFields = fieldsForActiveSection.filter((field) =>
      shouldDisplayField(field.name, evaluationValues),
    );
    const hiddenFields = fieldsForActiveSection.filter(
      (field) => !shouldDisplayField(field.name, evaluationValues),
    );

    const erroredFields = [];
    let hasMissingValues = false;

    visibleFields.forEach((field) => {
      const value = evaluationValues[field.name];
      const error = getValidationError(field, value, evaluationValues);
      updateFieldError(field.name, error);
      if (error) {
        erroredFields.push(field);
        if (error === REQUIRED_FIELD_MESSAGE) {
          hasMissingValues = true;
        }
      }
    });

    hiddenFields.forEach((field) => {
      updateFieldError(field.name, null);
    });

    if (erroredFields.length > 0) {
      setEditingFields((prev) => {
        const next = { ...prev };
        erroredFields.forEach((field) => {
          next[field.name] = true;
        });
        return next;
      });
      setErrorMessage(
        hasMissingValues
          ? 'Completa todos los campos de esta sección antes de guardar.'
          : 'Corrige los campos marcados en rojo antes de guardar.',
      );
      setSuccessMessage('');
      return false;
    }

    const preparedFormValues = needsNormalization
      ? { ...formValues, ...normalizedUpdates }
      : formValues;
    const adjustedFormValues = applyDependentDefaults(preparedFormValues);

    setFormValues(adjustedFormValues);

    setIsSaving(true);
    setErrorMessage('');
    setSuccessMessage('');

    const payload = buildUsuarioPayload(adjustedFormValues);

    try {
      const updated = await saveUsuario(payload);
      const mapped = mapUsuarioToFormValues(updated);
      const normalized = normalizeFormValues(mapped);
      const adjusted = applyDependentDefaults(normalized);
      setFormValues({ ...adjusted });
      setOriginalValues({ ...adjusted });
      setEditingFields({});
      setSuccessMessage('Información actualizada correctamente.');
      setFieldErrors({});
      return true;
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : 'No fue posible actualizar la información. Intenta nuevamente.';
      setErrorMessage(message);
      const normalizedMessage = message.toLowerCase();
      FIELD_CONFIG.filter((field) => field.type === 'number').forEach((field) => {
        if (
          normalizedMessage.includes(field.name.toLowerCase()) ||
          normalizedMessage.includes(field.label.toLowerCase()) ||
          normalizedMessage.includes('número') ||
          normalizedMessage.includes('numero')
        ) {
          updateFieldError(field.name, INTEGER_ONLY_MESSAGE);
        }
      });
      FIELD_CONFIG.filter((field) => DIGIT_ONLY_FIELDS.has(field.name)).forEach((field) => {
        if (
          normalizedMessage.includes(field.name.toLowerCase()) ||
          normalizedMessage.includes(field.label.toLowerCase()) ||
          normalizedMessage.includes('número') ||
          normalizedMessage.includes('numero')
        ) {
          updateFieldError(field.name, INTEGER_ONLY_MESSAGE);
        }
      });
      FIELD_CONFIG.filter((field) => LETTER_ONLY_FIELDS.has(field.name)).forEach((field) => {
        if (
          normalizedMessage.includes(field.name.toLowerCase()) ||
          normalizedMessage.includes(field.label.toLowerCase()) ||
          normalizedMessage.includes('letra')
        ) {
          updateFieldError(field.name, LETTER_FIELD_MESSAGE);
        }
      });
      FIELD_CONFIG.filter((field) => PHONE_FIELDS.has(field.name)).forEach((field) => {
        if (
          normalizedMessage.includes(field.name.toLowerCase()) ||
          normalizedMessage.includes('teléfono') ||
          normalizedMessage.includes('telefono') ||
          normalizedMessage.includes('celular')
        ) {
          updateFieldError(field.name, PHONE_FIELD_MESSAGE);
        }
      });
      FIELD_CONFIG.filter((field) => EMAIL_FIELDS.has(field.name)).forEach((field) => {
        if (
          normalizedMessage.includes(field.name.toLowerCase()) ||
          normalizedMessage.includes('correo') ||
          normalizedMessage.includes('email')
        ) {
          updateFieldError(field.name, EMAIL_FIELD_MESSAGE);
        }
      });
      if (
        normalizedMessage.includes('departamento de expedición') ||
        normalizedMessage.includes('departamento de expedicion')
      ) {
        updateFieldError(
          'departamentoExpedicion',
          'Selecciona el departamento de expedición para documentos expedidos en Colombia.',
        );
      }
      if (
        normalizedMessage.includes('ciudad de expedición') ||
        normalizedMessage.includes('ciudad de expedicion')
      ) {
        const targetField = shouldDisplayField('ciudadExpedicionId', formValues)
          ? 'ciudadExpedicionId'
          : 'ciudadExpedicionOtra';
        updateFieldError(
          targetField,
          shouldDisplayField('ciudadExpedicionId', formValues)
            ? 'Selecciona la ciudad de expedición para documentos expedidos en Colombia.'
            : 'Ingresa la ciudad de expedición del país seleccionado.',
        );
      }
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    await saveChanges();
  };

  const handleResetSection = () => {
    setFormValues((prev) => {
      const next = { ...prev };
      fieldsForActiveSection.forEach((field) => {
        next[field.name] = originalValues[field.name] ?? '';
      });
      return next;
    });
    setEditingFields((prev) => {
      const next = { ...prev };
      fieldsForActiveSection.forEach((field) => {
        next[field.name] = false;
      });
      return next;
    });
    setErrorMessage('');
    setSuccessMessage('');
    setFieldErrors((prev) => {
      const next = { ...prev };
      fieldsForActiveSection.forEach((field) => {
        delete next[field.name];
      });
      return next;
    });
  };

  const requestSectionChange = (sectionId) => {
    if (sectionId === activeSection) return;
    if (isDirty) {
      setPendingSection(sectionId);
      setShowUnsavedPrompt(true);
      return;
    }
    setActiveSection(sectionId);
    setEditingFields({});
    setErrorMessage('');
    setSuccessMessage('');
    setFieldErrors({});
  };

  const cancelSectionChange = () => {
    setPendingSection(null);
    setShowUnsavedPrompt(false);
  };

  const discardAndSwitch = () => {
    setFormValues({ ...originalValues });
    setEditingFields({});
    setErrorMessage('');
    setSuccessMessage('');
    setFieldErrors({});
    if (pendingSection) {
      setActiveSection(pendingSection);
    }
    setPendingSection(null);
    setShowUnsavedPrompt(false);
  };

  const saveAndSwitch = async () => {
    const ok = await saveChanges();
    if (!ok) return;
    if (pendingSection) {
      setActiveSection(pendingSection);
    }
    setPendingSection(null);
    setShowUnsavedPrompt(false);
    setFieldErrors({});
  };

  return (
    <DashboardLayout>
      <div className="px-[5%] pt-10 pb-8">
        <h1 className="text-4xl font-bold text-[#002E6D]">Mi cuenta: información básica</h1>
        <p className="mt-2 text-base text-[#2d4a7c]">
          Revisa y actualiza los datos que tenemos registrados para personalizar tu experiencia.
        </p>

        {needsProfileCompletion ? (
          <div className="mt-8 rounded-3xl bg-[#d7e6ff] p-6 text-[#032c65] shadow">
            <p className="text-lg font-semibold">
              Completa tu información inicial para habilitar la edición desde este panel.
            </p>
            <p className="mt-2 text-sm font-medium">
              Has guardado {completedCount} de {totalSections} etapas. Te faltan {remainingSections}{' '}
              etapa{remainingSections === 1 ? '' : 's'} por completar antes de habilitar la edición
              manual.
            </p>
            <p className="mt-2 text-sm font-medium">
              El formulario se abrirá automáticamente. Puedes cerrarlo una vez guardes todas las
              etapas.
            </p>
            <button
              type="button"
              onClick={handleFlowOpen}
              className="mt-4 rounded-xl bg-[#0054c9] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#2f6edc] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#002E6D]/60"
            >
              Completar información
            </button>
          </div>
        ) : (
          <div className="mt-8 flex flex-col gap-6 lg:flex-row">
            <nav className="flex shrink-0 flex-row gap-2 overflow-x-auto rounded-3xl border border-gray-200 bg-white/70 p-4 text-sm text-[#002E6D] shadow-sm lg:w-64 lg:flex-col">
              {SECTION_CONFIG.map((section) => {
                const isActive = section.id === activeSection;
                return (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => requestSectionChange(section.id)}
                    className={`rounded-2xl px-4 py-2 text-left font-semibold transition ${
                      isActive
                        ? 'bg-[#0054c9] text-white shadow'
                        : 'bg-transparent text-[#002E6D] hover:bg-[#d7e6ff]'
                    }`}
                  >
                    {section.label}
                  </button>
                );
              })}
            </nav>

            <form
              onSubmit={handleSubmit}
              className="flex-1 rounded-3xl bg-gray-200/70 p-6 shadow-inner lg:p-8"
            >
            {showUnsavedPrompt && (
              <div className="mb-6 rounded-2xl border border-amber-300 bg-amber-100 px-4 py-3 text-sm text-amber-900">
                Tienes cambios sin guardar. ¿Deseas guardarlos antes de cambiar de sección?
                <div className="mt-3 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={saveAndSwitch}
                    className="rounded-lg bg-[#0054c9] px-3 py-1 text-xs font-semibold text-white transition hover:bg-[#2f6edc]"
                    disabled={isSaving}
                  >
                    Guardar y continuar
                  </button>
                  <button
                    type="button"
                    onClick={discardAndSwitch}
                    className="rounded-lg border border-[#0054c9] px-3 py-1 text-xs font-semibold text-[#0054c9] transition hover:bg-[#0054c9] hover:text-white"
                    disabled={isSaving}
                  >
                    Descartar cambios
                  </button>
                  <button
                    type="button"
                    onClick={cancelSectionChange}
                    className="rounded-lg border border-transparent px-3 py-1 text-xs font-semibold text-[#002E6D] transition hover:bg-white/70"
                    disabled={isSaving}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}

            {isLoading ? (
              <div className="flex h-40 items-center justify-center text-[#002E6D]">
                Cargando tu información...
              </div>
            ) : (
              <>
                <div className="flex flex-col gap-3">
                  {visibleFieldsForActiveSection.length === 0 ? (
                    <div className="rounded-2xl border border-blue-200 bg-[#d7e6ff] px-4 py-3 text-sm text-[#032c65]">
                      No hay campos disponibles para esta sección con la configuración actual.
                    </div>
                  ) : (
                    visibleFieldsForActiveSection.map((field) => {
                      const isEditing = Boolean(editingFields[field.name]);
                      const isReadOnlyField =
                        field.inputProps?.readOnly === true &&
                        !field.meta?.addressGroupRoot;
                      return (
                        <div
                          key={field.name}
                          className="rounded-2xl border border-blue-200 bg-[#d7e6ff] px-4 py-3 shadow-sm"
                        >
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                            <div className="text-sm font-semibold text-[#002E6D] sm:w-56">
                              {field.label}
                            </div>
                            <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                              <div className="flex-1">
                                {isEditing ? (
                                  renderInput(field)
                                ) : (
                              <div className="w-full rounded-lg bg-[#caddff] px-3 py-2 text-sm text-[#0b2f66]">
                                {displayValue(field)}
                              </div>
                            )}
                          </div>
                              {isReadOnlyField ? null : (
                                <button
                                  type="button"
                                  onClick={() => toggleFieldEditing(field.name)}
                                  className="self-start rounded-lg border border-[#0054c9] px-3 py-1 text-xs font-semibold text-[#0054c9] transition hover:bg-[#0054c9] hover:text-white sm:self-auto"
                                >
                                  {isEditing ? 'Cerrar' : 'Editar'}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {errorMessage && (
                  <div className="mt-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {errorMessage}
                  </div>
                )}
                {successMessage && (
                  <div className="mt-6 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                    {successMessage}
                  </div>
                )}

                <div className="mt-6 flex flex-wrap gap-3">
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="rounded-xl bg-[#0054c9] px-5 py-2 text-sm font-semibold text-white transition hover:bg-[#2f6edc] disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {isSaving ? 'Guardando...' : 'Guardar cambios'}
                  </button>
                  <button
                    type="button"
                    onClick={handleResetSection}
                    disabled={isSaving}
                    className="rounded-xl border border-[#0054c9] px-5 py-2 text-sm font-semibold text-[#0054c9] transition hover:bg-[#0054c9] hover:text-white disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    Restaurar sección
                  </button>
                </div>
              </>
            )}
          </form>
        </div>
        )}
      </div>
      <ProfileCompletionFlow
        open={needsProfileCompletion && isFlowVisible}
        initialValues={flowInitialValues}
        completedSections={completedSections}
        onSaveSection={handleFlowSectionSave}
        onComplete={handleFlowComplete}
        onClose={handleFlowClose}
      />
    </DashboardLayout>
  );
};

export default PersonasAccount;
